if (!customElements.get('theme-drawer')) {
  const MAX_FOCUS_ATTEMPTS = 10;

  class ThemeDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = null;
      this.panel = null;
      this.previousActiveElement = null;
      this.focusableElements = null;
      this.cancelPendingFocus = null;
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.panel = this.querySelector('[data-drawer-panel]');
    }

    disconnectedCallback() {
      this._removeListeners();
      this._cancelFocusAttempts();
    }

    get side() {
      return this.dataset.side || 'right';
    }

    get trapFocus() {
      return this.dataset.trapFocus !== 'false';
    }

    get closeOnEsc() {
      return this.dataset.closeOnEsc !== 'false';
    }

    get closeOnOverlayClick() {
      return this.dataset.closeOnOverlayClick !== 'false';
    }

    open() {
      this.previousActiveElement = document.activeElement;
      this.setAttribute('data-open', '');
      this._addListeners();

      this._cancelFocusAttempts();
      this.cancelPendingFocus = theme.utils.afterPaint(() => this._attemptFocus(0));

      this._publish(theme?.pubsub?.events?.DRAWER_OPEN);
    }

    close() {
      this.removeAttribute('data-open');
      this._removeListeners();
      this._cancelFocusAttempts();

      this.previousActiveElement?.focus();
      this.previousActiveElement = null;

      this._publish(theme?.pubsub?.events?.DRAWER_CLOSE);
    }

    toggle() {
      this.hasAttribute('data-open') ? this.close() : this.open();
    }

    _addListeners() {
      document.addEventListener('keydown', this.handleKeyDown);
      if (this.closeOnOverlayClick) {
        this.overlay?.addEventListener('click', this.handleOverlayClick);
      }
    }

    _removeListeners() {
      document.removeEventListener('keydown', this.handleKeyDown);
      this.overlay?.removeEventListener('click', this.handleOverlayClick);
    }

    handleKeyDown(e) {
      if (e.key === 'Escape' && this.closeOnEsc) {
        this.close();
        return;
      }

      if (e.key === 'Tab' && this.trapFocus) {
        this._handleTabKey(e);
      }
    }

    handleOverlayClick() {
      this.close();
    }

    _handleTabKey(e) {
      this._updateFocusableElements();
      const first = this.focusableElements?.[0];
      const last = this.focusableElements?.[this.focusableElements.length - 1];

      if (!(this.panel ?? this).contains(document.activeElement)) {
        this._cancelFocusAttempts();
        (e.shiftKey ? last : first)?.focus();
        e.preventDefault();
        return;
      }

      if (e.shiftKey && document.activeElement === first) {
        last?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first?.focus();
        e.preventDefault();
      }
    }

    refreshFocus() {
      this._updateFocusableElements();
    }

    _attemptFocus(attempt) {
      this.cancelPendingFocus = null;

      if (!this.hasAttribute('data-open') || !this.isConnected) return;
      if (this.contains(document.activeElement)) return;

      this._updateFocusableElements();
      const target = this.focusableElements?.[0];
      if (!target) return;

      target.focus();

      if (this.contains(document.activeElement)) return;
      if (attempt >= MAX_FOCUS_ATTEMPTS - 1) return;

      this.cancelPendingFocus = theme.utils.afterPaint(() => this._attemptFocus(attempt + 1));
    }

    _cancelFocusAttempts() {
      this.cancelPendingFocus?.();
      this.cancelPendingFocus = null;
    }

    _updateFocusableElements() {
      const selector = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
      this.focusableElements = [...(this.panel ?? this).querySelectorAll(selector)].filter(
        el => !el.hidden && !el.disabled && el.offsetParent !== null
      );
    }

    _publish(eventName) {
      if (!eventName || typeof theme === 'undefined' || !theme.pubsub) return;
      theme.pubsub.publish(eventName, { id: this.id });
    }
  }

  customElements.define('theme-drawer', ThemeDrawer);
}
